"use client";

// Visual availability/booking calendar with two views:
//   Month  — full-size month grid
//   Year   — 12 mini-months, same markers, each day still clickable
//
// Day markers (color = status, texture = booking type):
//   emerald          available (inside an availability window, nothing booked)
//   rose solid       booked — PRIVATE (exclusive) booking
//   rose striped     booked — SHARED (prorata) booking
//   amber dashed     pending request
//   grey             past day / outside any availability window

import { useMemo, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPatch, apiDelete } from "@/lib/api-client";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Home, Users2, Lock, Plus, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/components/booking/format";
import { useI18n } from "@/lib/i18n/provider";

type CalendarProperty = {
  id: string;
  title: string;
  propertyType: string;
  availabilities: { startDate: string; endDate: string }[];
  bookings: {
    id: string;
    userId: string;
    startDate: string;
    endDate: string;
    status: "APPROVED" | "PENDING";
    partySize: number;
    bookingType: string;
    guestName: string;
  }[];
};

const DAY_MS = 86400000;

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function isoDay(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}
/** DST-safe: add n whole calendar days to a date. */
function addCalendarDays(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}
/** Availability windows / reservations end on the checkout day (inclusive):
 *  the stored end is the last millisecond of the last occupied day, so the
 *  end date itself IS marked. Compared as calendar-date keys: grid cells are
 *  local midnights while stored dates are UTC midnights, so raw instants can
 *  mismatch by the TZ offset. */
function coversDayKey(startIso: string, endIso: string, dayKey: string) {
  return dayKey >= startIso.slice(0, 10) && dayKey <= endIso.slice(0, 10);
}

type DayCell = {
  day: Date;
  inMonth: boolean;
  isPast: boolean;
  marker:
    | "available"
    | "bookedPrivate"
    | "bookedShared"
    | "pending"
    | "off";
  tooltips: string[];
  // Guests registered for that day, in the order they should be stacked
  // (index 0 = bottom line of the cell).
  names: { name: string; status: "APPROVED" | "PENDING" }[];
};

// How many guest names fit in a full-size month cell (plus an ellipsis line).
const MAX_CELL_NAMES = 3;

function buildMonthGrid(
  property: CalendarProperty,
  month: Date,
  today: Date
): DayCell[] {
  const first = startOfMonth(month);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  // DST-safe: build each cell from calendar-day arithmetic (fixed 24h steps
  // duplicate a date across the October clock change).
  return Array.from({ length: 42 }, (_, i) => {
    const day = addCalendarDays(first, i - startOffset);
    const dayKey = isoDay(day);
    const inMonth = day.getMonth() === month.getMonth();
    const isPast = day.getTime() < today.getTime();
    const available = property.availabilities.some((a) =>
      coversDayKey(a.startDate, a.endDate, dayKey)
    );
    const approved = property.bookings.filter(
      (b) => b.status === "APPROVED" && coversDayKey(b.startDate, b.endDate, dayKey)
    );
    const pending = property.bookings.filter(
      (b) => b.status === "PENDING" && coversDayKey(b.startDate, b.endDate, dayKey)
    );
    let marker: DayCell["marker"] = "off";
    if (approved.some((b) => b.bookingType === "PRIVATE")) marker = "bookedPrivate";
    else if (approved.some((b) => b.bookingType === "SHARED")) marker = "bookedShared";
    else if (pending.length > 0) marker = "pending";
    else if (available) marker = "available";
    const tooltips = [
      ...approved.map(
        (b) => `${b.guestName} · ${b.partySize}p · ${b.bookingType} · ${b.status}`
      ),
      ...pending.map((b) => `${b.guestName || "?"} · ${b.partySize}p · PENDING`),
    ];
    const names = [
      ...approved.map((b) => ({ name: b.guestName || "?", status: "APPROVED" as const })),
      ...pending.map((b) => ({ name: b.guestName || "?", status: "PENDING" as const })),
    ];
    return { day, inMonth, isPast, marker, tooltips, names };
  });
}

const CELL_CLS: Record<DayCell["marker"], string> = {
  available:
    "bg-zinc-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-800",
  bookedPrivate:
    "bg-zinc-100 text-rose-900 border-rose-400 dark:bg-rose-950 dark:text-rose-200 dark:border-rose-900",
  bookedShared:
    "bg-sky-50 text-sky-900 border-sky-500 dark:bg-sky-950 dark:text-sky-200 dark:border-sky-700",
  pending:
    "bg-zinc-100 text-amber-900 border-amber-400 border-dashed dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800",
  off: "bg-zinc-100 text-zinc-400 border-zinc-200 dark:bg-zinc-900 dark:text-zinc-500 dark:border-zinc-800",
};

export function AvailabilityCalendar() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [propertyId, setPropertyId] = useState<string>("");
  const [view, setView] = useState<"month" | "year">("month");
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const permissions = useAppStore((s) => s.permissions);
  const canBook = permissions.includes("bookings.manage");
  // Approve/reject: server allows ADMIN anywhere, OWNER on their own
  // properties. The calendar route already lists only the owner's properties
  // for owners, so role-level check is enough here.
  const user = useAppStore((s) => s.user);
  const canValidate = !!user && (user.role === "ADMIN" || user.role === "OWNER");
  const [bookingDay, setBookingDay] = useState<Date | null>(null);
  const [bookForm, setBookForm] = useState({
    bookingType: "PRIVATE" as "PRIVATE" | "SHARED",
    start: "",
    end: "",
    partySize: 1,
    userId: "",
  });
  const [busy, setBusy] = useState(false);


  const { data, isLoading } = useQuery({
    queryKey: ["calendar"],
    queryFn: () => apiGet<{ properties: CalendarProperty[] }>("/api/admin/calendar"),
  });

  const properties = data?.properties ?? [];
  const selectedId = propertyId || properties[0]?.id || "";
  const property = properties.find((p) => p.id === selectedId);

  const { data: dashData } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () =>
      apiGet<{ users: { id: string; name: string; username: string; role: string }[] }>(
        "/api/admin/dashboard"
      ),
    enabled: !!bookingDay,
  });
  const bookableUsers = dashData?.users ?? [];

  const createBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!property || !bookForm.start || !bookForm.end) return;
    setBusy(true);
    try {
      await apiPost("/api/admin/bookings", {
        productId: property.id,
        bookingType: bookForm.bookingType,
        userId: bookForm.userId || undefined,
        startDate: bookForm.start,
        endDate: bookForm.end,
        partySize: Math.max(1, bookForm.partySize),
      });
      toast.success(t("calendar.bookingAdded"));
      qc.invalidateQueries({ queryKey: ["calendar"] });
      setBookingDay(null);
    } catch (err: any) {
      toast.error(err.message || "Could not add booking");
    } finally {
      setBusy(false);
    }
  };

  const openBookingFor = (day: Date, preferred: "PRIVATE" | "SHARED" = "PRIVATE") => {
    setBookForm({
      bookingType: preferred,
      start: isoDay(day),
      end: isoDay(addCalendarDays(day, 1)),
      partySize: 1,
      userId: "",
    });
    setBookingDay(day);
  };

  // Clicking an occupied day shows what occupies it instead of the add form.
  const [dayDetails, setDayDetails] = useState<Date | null>(null);
  // Which reservation is currently being approved/rejected (disable buttons).
  const [decideId, setDecideId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<
    CalendarProperty["bookings"][number] | null
  >(null);
  const openDay = (day: Date) => {
    const dayKey = isoDay(day);
    const hasBookings = property?.bookings.some(
      (b) =>
        (b.status === "APPROVED" || b.status === "PENDING") &&
        coversDayKey(b.startDate, b.endDate, dayKey)
    );
    if (hasBookings) setDayDetails(day);
    else if (canBook) openBookingFor(day);
  };

  const dayBookings = useMemo(() => {
    if (!property || !dayDetails) return [];
    const dayKey = isoDay(dayDetails);
    return property.bookings
      .filter(
        (b) =>
          (b.status === "APPROVED" || b.status === "PENDING") &&
          coversDayKey(b.startDate, b.endDate, dayKey)
      )
      .sort((a, b) => (a.status === b.status ? b.partySize - a.partySize : a.status === "APPROVED" ? -1 : 1));
  }, [property, dayDetails]);

  const dayIsExclusive = dayBookings.some(
    (b) => b.bookingType === "PRIVATE" && b.status === "APPROVED"
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Which stay types are possible for the date range being booked?
  const approvedCoveringRange =
    property && bookForm.start && bookForm.end
      ? property.bookings.filter(
          (b) =>
            b.status === "APPROVED" &&
            b.startDate.slice(0, 10) < bookForm.end &&
            b.endDate.slice(0, 10) > bookForm.start
        )
      : [];
  const rangeHasAny = approvedCoveringRange.length > 0;
  const rangeHasPrivate = approvedCoveringRange.some((b) => b.bookingType === "PRIVATE");
  const privateAllowed = !rangeHasAny;
  const sharedAllowed = !rangeHasPrivate;
  const typeValid =
    bookForm.bookingType === "PRIVATE" ? privateAllowed : sharedAllowed;

  // Users already booked (pending/approved) on the selected date range.
  const bookedUserIds =
    property && bookForm.start && bookForm.end
      ? property.bookings
          .filter(
            (b) =>
              (b.status === "APPROVED" || b.status === "PENDING") &&
              b.startDate.slice(0, 10) < bookForm.end &&
              b.endDate.slice(0, 10) > bookForm.start
          )
          .map((b) => b.userId)
      : [];

  const monthDays = useMemo(
    () => (property ? buildMonthGrid(property, month, today) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [property, month]
  );

  const dateFmt = (opts: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(
      locale === "fr" ? "fr-FR" : locale === "de" ? "de-DE" : "en-GB",
      opts
    );

  const monthLabel = dateFmt({ month: "long", year: "numeric" }).format(month);
  const dayHeaders = useMemo(() => {
    const fmt = dateFmt({ weekday: "short" });
    return Array.from({ length: 7 }, (_, i) =>
      fmt.format(new Date(2024, 0, 1 + i))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const dayCell = (cell: DayCell, size: "full" | "mini", key: string) => {
    const { day, inMonth, isPast, marker, tooltips } = cell;
    const clickable = canBook && !isPast;
    const base =
      size === "full"
        ? "relative flex h-16 flex-col rounded-lg border p-1 text-start text-xs shadow-sm dark:shadow-none"
        : "relative flex aspect-square items-center justify-center rounded-[4px] border text-[9px]";
    const cls = `${base} ${CELL_CLS[marker]} ${inMonth ? "" : "opacity-60"} ${
      clickable ? "cursor-pointer transition-all hover:ring-2 hover:ring-primary/40 hover:shadow-md hover:-translate-y-px" : ""
    }`;
    const title = tooltips.length > 0 ? tooltips.join("\n") : undefined;

    const content =
      size === "full" ? (
        <>
          <span
            className={`font-medium ${
              day.getTime() === today.getTime()
                ? "underline decoration-2 underline-offset-2"
                : ""
            }`}
          >
            {day.getDate()}
          </span>
          {cell.names.length > 0 ? (
            <span className="mt-auto flex flex-col justify-end gap-[1px] text-[9px] leading-[10px]">
              {cell.names.length > MAX_CELL_NAMES && (
                <span className="font-semibold">…</span>
              )}
              {cell.names
                .slice(0, MAX_CELL_NAMES)
                .reverse()
                .map((n, idx) => (
                  <span
                    key={idx}
                    className={`truncate ${
                      n.status === "PENDING"
                        ? "text-amber-800 dark:text-amber-300"
                        : ""
                    }`}
                  >
                    {n.name}
                  </span>
                ))}
            </span>
          ) : marker === "available" && (
            <span className="mt-auto truncate text-[10px] leading-tight text-emerald-800">
              {t("calendar.availableShort")}
            </span>
          )}
        </>
      ) : (
        <span className="font-medium">{day.getDate()}</span>
      );

    return clickable ? (
      <button
        key={key}
        type="button"
        title={title}
        onClick={() => openDay(day)}
        className={cls}
      >
        {content}
      </button>
    ) : (
      <div key={key} title={title} className={cls}>
        {content}
      </div>
    );
  };

  return (
    <div>
      {/* Controls */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={selectedId}
            onValueChange={setPropertyId}
            disabled={properties.length === 0}
          >
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder={t("calendar.chooseProperty")} />
            </SelectTrigger>
            <SelectContent>
              {properties.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {property && (
            <Badge variant="outline" className="capitalize">
              {property.title.length > 24
                ? property.title.slice(0, 24) + "…"
                : property.title}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-md border border-foreground">
            <Button
              size="sm"
              variant="ghost"
              className={`h-8 rounded-none border-0 ${
                view === "month"
                  ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
                  : "bg-background text-foreground hover:bg-muted"
              }`}
              onClick={() => setView("month")}
            >
              {t("calendar.viewMonth")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className={`h-8 rounded-none border-0 ${
                view === "year"
                  ? "bg-foreground text-background hover:bg-foreground/90 hover:text-background"
                  : "bg-background text-foreground hover:bg-muted"
              }`}
              onClick={() => setView("year")}
            >
              {t("calendar.viewYear")}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="icon"
              variant="outline"
              onClick={() =>
                view === "month"
                  ? setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))
                  : setMonth(new Date(month.getFullYear() - 1, month.getMonth(), 1))
              }
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[130px] text-center text-sm font-medium capitalize">
              {view === "month" ? monthLabel : month.getFullYear()}
            </span>
            <Button
              size="icon"
              variant="outline"
              onClick={() =>
                view === "month"
                  ? setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))
                  : setMonth(new Date(month.getFullYear() + 1, month.getMonth(), 1))
              }
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-emerald-300 bg-zinc-100" />
          {t("calendar.legendAvailable")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-rose-400 bg-zinc-100" />
          {t("calendar.legendBookedPrivate")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-sky-500 bg-sky-50" />
          {t("calendar.legendBookedShared")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-dashed border-amber-400 bg-zinc-100" />
          {t("calendar.legendPending")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded border border-zinc-300 bg-zinc-100" />
          {t("calendar.legendOff")}
        </span>
      </div>

      {isLoading ? (
        <Skeleton className="h-72 w-full rounded-xl" />
      ) : !property ? (
        <div className="grid place-items-center rounded-xl border border-dashed py-12 text-sm text-muted-foreground">
          {t("calendar.noProperties")}
        </div>
      ) : view === "month" ? (
        <div>
          <div className="mb-1 grid grid-cols-7 gap-1">
            {dayHeaders.map((d) => (
              <div
                key={d}
                className="text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthDays.map((cell, i) => dayCell(cell, "full", String(i)))}
          </div>
        </div>
      ) : (
        /* Year view: 12 mini-months, same markers, days clickable */
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 12 }, (_, m) => {
            const miniMonth = new Date(month.getFullYear(), m, 1);
            const cells = buildMonthGrid(property, miniMonth, today);
            const bookedDays = cells.filter(
              (c) =>
                c.inMonth &&
                (c.marker === "bookedPrivate" || c.marker === "bookedShared")
            ).length;
            const pendingDays = cells.filter(
              (c) => c.inMonth && c.marker === "pending"
            ).length;
            return (
              <div key={m} className="rounded-lg border bg-card p-2">
                <button
                  type="button"
                  className="mb-1 flex w-full items-center justify-between rounded px-1 text-xs font-semibold capitalize hover:bg-muted"
                  title={canBook ? t("calendar.bookMonth") : undefined}
                  onClick={() =>
                    canBook ? openBookingFor(miniMonth) : undefined
                  }
                  disabled={!canBook}
                >
                  <span>{dateFmt({ month: "long" }).format(miniMonth)}</span>
                  <span className="flex items-center gap-1 text-[10px] font-normal text-muted-foreground">
                    {bookedDays > 0 && (
                      <span className="text-rose-600 dark:text-rose-400">
                        {bookedDays}
                        {t("calendar.daySuffix")}
                      </span>
                    )}
                    {pendingDays > 0 && (
                      <span className="text-amber-600 dark:text-amber-400">
                        {pendingDays}
                        {t("calendar.daySuffixP")}
                      </span>
                    )}
                  </span>
                </button>
                <div className="grid grid-cols-7 gap-[3px]">
                  {cells.map((cell, i) => dayCell(cell, "mini", `${m}-${i}`))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {canBook && (
        <p className="mt-2 text-xs text-muted-foreground">
          {view === "month" ? t("calendar.clickHint") : t("calendar.clickHintYear")}
        </p>
      )}

      {/* Day details: what occupies a clicked, already-occupied day */}
      <Dialog open={!!dayDetails} onOpenChange={(o) => !o && setDayDetails(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t("calendar.dayDetails", {
                date: dayDetails
                  ? dateFmt({ day: "numeric", month: "long", year: "numeric" }).format(dayDetails)
                  : "",
              })}
            </DialogTitle>
            <DialogDescription>{property?.title}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            {dayBookings.map((b) => (
              <div key={b.id} className="rounded-lg border bg-card px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 font-medium">
                    {b.bookingType === "SHARED" ? (
                      <Users2 className="h-4 w-4 text-sky-600" />
                    ) : (
                      <Lock className="h-4 w-4 text-rose-500" />
                    )}
                    {b.guestName || "—"}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge
                      variant="outline"
                      className={
                        b.status === "APPROVED"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-amber-200 bg-amber-50 text-amber-800"
                      }
                    >
                      {b.status === "APPROVED"
                        ? t("calendar.status.APPROVED")
                        : t("calendar.status.PENDING")}
                    </Badge>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      title={t("calendar.cancelBooking")}
                      onClick={() => setCancelTarget(b)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {b.bookingType === "SHARED"
                    ? t("booking.sharedTitle")
                    : t("booking.privateTitle")}{" "}
                  · {t("guests")}: {b.partySize} · {formatDate(b.startDate, locale)} —{" "}
                  {formatDate(b.endDate, locale)}
                </div>
              </div>
            ))}
            {dayIsExclusive && (
              <p className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
                {t("calendar.exclusiveNote")}
              </p>
            )}
          </div>
          {!dayIsExclusive && canBook && dayDetails && (
            <DialogFooter>
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={() => {
                  const d = dayDetails;
                  setDayDetails(null);
                  openBookingFor(d);
                }}
              >
                <Plus className="h-4 w-4" /> {t("calendar.addHere")}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" /> {t("calendar.cancelTitle")}
            </DialogTitle>
            <DialogDescription>
              {cancelTarget
                ? `${cancelTarget.guestName || "—"} · ${formatDate(
                    cancelTarget.startDate,
                    locale
                  )} — ${formatDate(cancelTarget.endDate, locale)} · ${t(
                    `booking.${cancelTarget.bookingType}`
                  )}`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
            {t("calendar.cancelAck")}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!cancelTarget) return;
                setBusy(true);
                try {
                  const res = await apiPatch(
                    `/api/reservations/${cancelTarget.id}`,
                    { action: "cancel" }
                  );
                  toast.success(t("calendar.cancelled"));
                  setCancelTarget(null);
                  setDayDetails(null);
                  qc.invalidateQueries({ queryKey: ["calendar"] });
                  void res;
                } catch (err: any) {
                  toast.error(err.message || "Could not cancel");
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t("calendar.cancelConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add booking from a clicked day / month */}
      <Dialog open={!!bookingDay} onOpenChange={(o) => !o && setBookingDay(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("calendar.addBooking")}</DialogTitle>
            <DialogDescription>
              {property?.title} ·{" "}
              {bookingDay
                ? t("calendar.bookDay", {
                    date: dateFmt({
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    }).format(bookingDay),
                  })
                : ""}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createBooking} className="grid gap-4">
            {/* Booking type chooser */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setBookForm((f) => ({ ...f, bookingType: "PRIVATE" }))}
                className={`rounded-lg border p-2.5 text-start text-sm transition ${
                  bookForm.bookingType === "PRIVATE"
                    ? "border-rose-400 bg-rose-50 text-rose-900 ring-1 ring-rose-300"
                    : "border-border bg-card text-muted-foreground hover:border-sky-200"
                }`}
              >
                <div className="font-medium">{t("booking.privateTitle")}</div>
                <div className="mt-0.5 text-[11px] leading-tight">{t("booking.PRIVATE.short")}</div>
              </button>
              <button
                type="button"
                onClick={() => setBookForm((f) => ({ ...f, bookingType: "SHARED" }))}
                className={`rounded-lg border p-2.5 text-start text-sm transition ${
                  bookForm.bookingType === "SHARED"
                    ? "border-sky-400 bg-sky-50 text-sky-900 ring-1 ring-sky-300"
                    : "border-border bg-card text-muted-foreground hover:border-sky-200"
                }`}
              >
                <div className="font-medium">{t("booking.sharedTitle")}</div>
                <div className="mt-0.5 text-[11px] leading-tight">{t("booking.SHARED.short")}</div>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>{t("detail.checkIn")}</Label>
                <Input
                  type="date"
                  required
                  value={bookForm.start}
                  onChange={(e) =>
                    setBookForm((f) => ({
                      ...f,
                      start: e.target.value,
                      end: f.end && f.end <= e.target.value ? "" : f.end,
                    }))
                  }
                />
              </div>
              <div className="grid gap-1.5">
                <Label>{t("detail.checkOut")}</Label>
                <Input
                  type="date"
                  required
                  min={bookForm.start}
                  value={bookForm.end}
                  onChange={(e) => setBookForm((f) => ({ ...f, end: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>{t("guests")}</Label>
                <Input
                  type="number"
                  min={1}
                  required
                  value={bookForm.partySize}
                  onChange={(e) =>
                    setBookForm((f) => ({ ...f, partySize: Number(e.target.value) || 1 }))
                  }
                />
              </div>
              <div className="grid gap-1.5">
                <Label>{t("calendar.bookFor")}</Label>
                <Select
                  value={bookForm.userId || "myself"}
                  onValueChange={(v) =>
                    setBookForm((f) => ({ ...f, userId: v === "myself" ? "" : v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="myself">{t("calendar.myself")}</SelectItem>
                    {bookableUsers.map((u) => {
                      const already = bookedUserIds.includes(u.id);
                      return (
                        <SelectItem key={u.id} value={u.id} disabled={already}>
                          {u.name} (@{u.username})
                          {already ? ` — ${t("calendar.alreadyBooked")}` : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t("calendar.approvedHint")}</p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBookingDay(null)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={busy || !typeValid} className="bg-emerald-600 hover:bg-emerald-700">
                {t("calendar.addBooking")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function CalendarPropertyIcon() {
  return <Home className="h-4 w-4" />;
}
